import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal/Modal';
import { RadioGroup } from '@/components/ui/Radio';
import { showToast } from '@/components/ui/Toast';
import { Plus, Pencil, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { t, __resolve } from '@/lib/i18n';
import { PersonaFormDialog } from '@/routes/roros/overarching/PersonaFormDialog';
import {
  useRorosPersonas,
  useRorosPersonaCreate,
  useRorosPersonaUpdate,
  useRorosPersonaRemove,
  useRorosIcpPersonaLinks,
  useRorosIcpPersonaLinkCreate,
  useRorosIcpPersonaLinkRemove,
  useRorosIcpPersonaLinkReorder,
} from '@/hooks/roros/overarching';
import type {
  RorosPersonaDTO,
  RorosIcpPersonaLinkDTO,
  CreatePersonaPayload,
} from '@/types/roros/overarching';
import { useOverarchingEditable } from '@/routes/roros/overarching/OverarchingEditableContext';
import { AddPersonaModal } from './AddPersonaModal';

// ─── Personas linked to an ICP ───────────────────────────────────────────────

export function PersonaLinkList({ icpId }: { icpId: string }) {
  const linksQuery = useRorosIcpPersonaLinks({ icpId });
  const personasQuery = useRorosPersonas({ pageSize: '100' });

  const createLink = useRorosIcpPersonaLinkCreate();
  const removeLink = useRorosIcpPersonaLinkRemove();
  const reorderLinks = useRorosIcpPersonaLinkReorder();
  const createPersona = useRorosPersonaCreate();
  const updatePersona = useRorosPersonaUpdate();
  const removePersona = useRorosPersonaRemove();
  const { canEdit } = useOverarchingEditable();

  const links = (linksQuery.data ?? []) as RorosIcpPersonaLinkDTO[];
  const allPersonas = (personasQuery.data ?? []) as RorosPersonaDTO[];
  const personaById = useMemo(() => {
    const map = new Map<string, RorosPersonaDTO>();
    for (const p of allPersonas) map.set(p.id, p);
    return map;
  }, [allPersonas]);

  const [adding, setAdding] = useState(false);
  const [editingPersona, setEditingPersona] = useState<RorosPersonaDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<
    | null
    | { linkId: string; personaId: string; personaName: string }
  >(null);
  const [deleteMode, setDeleteMode] = useState<'link' | 'catalog'>('link');

  async function move(index: number, direction: 'up' | 'down') {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === links.length - 1) return;
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    try {
      await reorderLinks.mutateAsync([
        { id: links[index].id, sortOrder: links[swapIdx].sortOrder },
        { id: links[swapIdx].id, sortOrder: links[index].sortOrder },
      ]);
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to reorder'));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      if (deleteMode === 'catalog') {
        await removePersona.mutateAsync(deleteTarget.personaId);
      } else {
        await removeLink.mutateAsync(deleteTarget.linkId);
      }
      setDeleteTarget(null);
      setDeleteMode('link');
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to delete'));
    }
  }

  async function addFromCatalog(personaId: string) {
    try {
      await createLink.mutateAsync({ icpId, personaId });
      setAdding(false);
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to link persona'));
    }
  }

  async function createAndLink(draft: CreatePersonaPayload) {
    try {
      const persona = await createPersona.mutateAsync(draft as unknown as Record<string, unknown>);
      const personaId = (persona as { id: string }).id; // TODO(wave-g): tighten hook return type
      try {
        await createLink.mutateAsync({ icpId, personaId });
      } catch (linkErr) {
        // best-effort rollback: if the link step fails, remove the just-created persona to
        // avoid orphaning it in the catalog. If rollback also fails, the persona stays as an
        // orphan that the user can delete manually from the catalog.
        try {
          await removePersona.mutateAsync(personaId);
        } catch { /* leave orphan; user can delete from catalog */ }
        throw linkErr;
      }
      setAdding(false);
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to create persona'));
    }
  }

  return (
    <div className="ml-6 border-l-2 border-border pl-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{t('Personas')}</h4>
        {canEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setAdding(true)}
          >
            {t('Add Persona')}
          </Button>
        )}
      </div>

      {links.length === 0 ? (
        <p className="text-xs text-muted-foreground italic mt-2">
          {t('No personas linked to this ICP yet.')}
        </p>
      ) : (
        <div className="space-y-2 mt-2">
          {links.map((link, index) => {
            const persona = personaById.get(link.personaId);
            const personaName = persona?.name ?? link.personaName ?? t('Unknown persona');
            return (
              <div key={link.id} className="rounded-md border border-border bg-background p-2">
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{personaName}</p>
                      {persona?.role && (
                        <p className="text-xs text-muted-foreground">{persona.role}</p>
                      )}
                      {persona?.decisionRole && (
                        <Badge variant="secondary" className="text-[10px] mt-1">
                          {t(persona.decisionRole)}
                        </Badge>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {persona && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={t('Edit')}
                            aria-label={t('Edit')}
                            onClick={() => setEditingPersona(persona)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === 0}
                          title={t('Move up')}
                          aria-label={t('Move up')}
                          onClick={() => void move(index, 'up')}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === links.length - 1}
                          title={t('Move down')}
                          aria-label={t('Move down')}
                          onClick={() => void move(index, 'down')}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10"
                          title={t('Delete')}
                          aria-label={t('Delete')}
                          onClick={() => {
                            setDeleteTarget({ linkId: link.id, personaId: link.personaId, personaName });
                            setDeleteMode('link');
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
              </div>
            );
          })}
        </div>
      )}

      <AddPersonaModal
        isOpen={adding}
        onClose={() => setAdding(false)}
        availablePersonas={allPersonas.filter((p) => !links.some((l) => l.personaId === p.id))}
        onPick={addFromCatalog}
        onCreate={createAndLink}
      />

      {/* Edit persona — uses the same PersonaFormBody as the Create flow in AddPersonaModal */}
      <PersonaFormDialog
        isOpen={!!editingPersona}
        onClose={() => setEditingPersona(null)}
        mode="edit"
        persona={editingPersona}
        onSubmit={async (payload) => {
          if (!editingPersona) return;
          try {
            await updatePersona.mutateAsync({ id: editingPersona.id, payload: payload as unknown as Record<string, unknown> });
            showToast.success(t('Persona updated'));
            setEditingPersona(null);
          } catch (e) {
            showToast.error(e instanceof Error ? e.message : t('Failed to update persona'));
          }
        }}
        isSubmitting={updatePersona.isPending}
      />

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('Remove persona')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>{t('Cancel')}</Button>
            <Button
              variant={deleteMode === 'catalog' ? 'destructive' : 'primary'}
              onClick={() => void confirmDelete()}
            >
              {deleteMode === 'catalog' ? t('Delete from catalog') : t('Remove from this ICP')}
            </Button>
          </div>
        }
      >
        {deleteTarget && (
          <div className="space-y-3">
            <p className="text-sm">
              {__resolve('ui::What should happen to {{name}}?', { name: deleteTarget.personaName })}
            </p>
            <RadioGroup
              name="delete-mode"
              value={deleteMode}
              onChange={(v) => setDeleteMode(v as 'link' | 'catalog')}
              options={[
                {
                  value: 'link',
                  label: t('Remove from this ICP only'),
                  hint: t('The persona stays in the catalog and in other ICPs.'),
                },
                {
                  value: 'catalog',
                  label: t('Delete from catalog entirely'),
                  hint: t('Removes the persona from every ICP and the catalog. Cannot be undone.'),
                  labelClassName: 'text-destructive',
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
